import { Link } from "react-router-dom";
import {
  ArrowRight,
  Shield,
  Award,
  Lock,
  Monitor,
  Clock,
  Briefcase,
  SearchCheck,
  Scale,
  Heart,
  Settings,
  ChevronRight,
  MapPin,
  Target,
  Eye,
  Users,
  FileText,
  AlertTriangle,
  Building2,
  Gavel,
  ShieldCheck,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useSiteConfig } from "../contexts/SiteConfigContext";

const serviceIconMap = {
  Briefcase,
  SearchCheck,
  Scale,
  Heart,
  Settings,
  MapPin,
  Target,
  Shield,
  Eye,
  Users,
  Lock,
  Award,
  Clock,
  Monitor,
  FileText,
  AlertTriangle,
  Building2,
  Gavel,
  ShieldCheck,
};

const chooseIconMap = {
  Award,
  Lock,
  Monitor,
  Clock,
};

export default function HomePage() {
  const { images, siteInfo, services, home } = useSiteConfig();
  const stats = home?.stats || [];
  const whyChooseUs = home?.whyChooseUs || [];

  return (
    <main>
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.hero}
            alt=""
            className="w-full h-full object-cover"
          />
          <div 
            className="absolute inset-0" 
            style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.8), rgba(0,0,0,0.4), transparent)' }}
          />
          <div 
            className="absolute inset-0" 
            style={{ background: 'linear-gradient(to top, #0A0A0F, transparent, rgba(0,0,0,0.3))' }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#DC2626]/30 bg-[#DC2626]/[0.08] mb-8 animate-fade-in">
              <div className="w-2 h-2 rounded-full bg-[#DC2626] animate-pulse" />
              <span className="text-[#DC2626] text-xs font-semibold tracking-[0.2em] uppercase">
                {home?.heroBadge || "Professional Polygraph Services"}
              </span>
            </div>

            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-6 animate-slide-up">
              {(home?.heroTitle || "The Lie\nDetective").split("\n").map((line, i) => (
                <span key={i}>
                  {i > 0 && <br />}
                  {i === 1 ? <span className="text-[#DC2626]">{line}</span> : line}
                </span>
              ))}
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 font-light tracking-[0.15em] mb-3 animate-slide-up-delay">
              {siteInfo.tagline}
            </p>

            <p className="text-gray-400 text-base md:text-lg leading-relaxed mb-10 max-w-xl animate-slide-up-delay-2">
              {home?.heroSubtitle || "South Africa's trusted polygraph examination service. Uncovering truth for corporates, legal professionals, and individuals with scientific precision and absolute discretion."}
            </p>

            <div className="flex flex-wrap gap-4 animate-slide-up-delay-2">
              <Link to="/services">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 px-8 h-13 text-base font-medium transition-colors duration-200"
                >
                  Our Services
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-8 h-13 text-base font-medium transition-colors duration-200"
                >
                  Request a Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce-slow">
          <span className="text-gray-500 text-xs tracking-widest uppercase">Scroll</span>
          <div className="w-px h-8 bg-gradient-to-b from-gray-500 to-transparent" />
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#0A0A0F] border-y border-white/[0.04] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 lg:gap-12">
            {stats.map((stat, i) => (
              <div key={i} className="text-center group">
                <p className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#DC2626] mb-2 font-display">
                  {stat.value}
                </p>
                <p className="text-gray-500 text-xs md:text-sm uppercase tracking-[0.15em]">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="bg-[#0A0A0F] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-xl mb-16">
            <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
              What We Offer
            </p>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Our Services
            </h2>
            <p className="text-gray-400 leading-relaxed">
              Comprehensive polygraph examination services tailored to your
              specific needs — from corporate screening to personal matters.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.slice(0, 3).map((service) => {
              const Icon = serviceIconMap[service.icon] || Shield;
              return (
                <div
                  key={service.id}
                  className="group bg-[#111118] border border-white/[0.04] rounded-2xl p-8 hover:border-[#DC2626]/20 transition-all duration-500 hover:-translate-y-1"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#DC2626]/[0.08] flex items-center justify-center mb-6 group-hover:bg-[#DC2626]/[0.15] transition-colors duration-300">
                    <Icon className="h-5 w-5 text-[#DC2626]" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-5">
                    {service.shortDescription}
                  </p>
                  <Link
                    to="/services"
                    className="text-[#DC2626] text-sm font-medium inline-flex items-center gap-1.5 hover:gap-2.5 transition-all duration-200"
                  >
                    Learn More
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="flex justify-center mt-12">
            <Link to="/services">
              <Button
                variant="outline"
                className="border-white/10 text-gray-300 hover:text-white hover:bg-white/[0.04] px-8 transition-colors duration-200"
              >
                View All Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-[#0D0D14] py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            <div>
              <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
                Why Choose Us
              </p>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6 leading-tight">
                Professional. Confidential.
                <br />
                <span className="text-[#DC2626]">Accurate.</span>
              </h2>
              <p className="text-gray-400 leading-relaxed mb-10">
                The Lie Detective delivers polygraph examination services built
                on a foundation of professional integrity, scientific
                methodology, and unwavering commitment to confidentiality.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {whyChooseUs.map((item, i) => {
                  const Icon = chooseIconMap[item.icon] || Shield;
                  return (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-[#DC2626]/[0.08] flex items-center justify-center shrink-0">
                        <Icon className="h-5 w-5 text-[#DC2626]" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-sm mb-1">
                          {item.title}
                        </h4>
                        <p className="text-gray-500 text-xs leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#DC2626]/[0.03] rounded-3xl blur-3xl" />
              <img
                src={images.detective}
                alt="Investigation"
                className="relative rounded-2xl w-full h-[480px] object-cover shadow-2xl shadow-black/50"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0D0D14] via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={images.evidenceBoard}
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-[#0A0A0F]/92" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-[#DC2626] font-semibold tracking-[0.3em] text-xs mb-4 uppercase">
            Get Started
          </p>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Uncover the Truth?
          </h2>
          <p className="text-gray-400 text-lg mb-10 leading-relaxed">
            Whether it's a corporate investigation, legal matter, or personal
            concern — we're here to help with professional, confidential
            polygraph services.
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white px-10 h-13 text-base font-medium transition-colors duration-200"
            >
              Get in Touch
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
}
